import React, { useState, type JSX, useCallback, useRef } from 'react';
import { GoogleMap, InfoWindow, Marker, MarkerClusterer } from '@react-google-maps/api';
import { Box, Card, CardContent, Divider, IconButton, Stack, Typography } from '@mui/material';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import Warning from '@mui/icons-material/Warning';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Snooze from '@mui/icons-material/Snooze';
import Block from '@mui/icons-material/Block';
import Info from '@mui/icons-material/Info';
import Schedule from '@mui/icons-material/Schedule';
import { CloudOffFilled } from '@brightlayer-ui/icons-mui';
import { useTheme } from '@mui/material/styles';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import Layers from '@mui/icons-material/Layers';
import MyLocation from '@mui/icons-material/MyLocation';
import ZoomIn from '@mui/icons-material/ZoomIn';
import ZoomOut from '@mui/icons-material/ZoomOut';
import { WidgetFooter, WidgetHeader } from 'pages/WidgetManagement/common';
import ReactDOMServer from 'react-dom/server';
import { useTypedSelector } from '@fiji/common/src/app/store';
import CustomIcon from 'components/CustomIcon';
import { mapStyles } from '@fiji/common';
import { useNavigate } from 'react-router-dom';
import { useIsMount, useNavigationBack, useRBAC } from 'hooks';
import { CustomTransComponent, Loader } from 'components';
import { useDispatch } from 'react-redux';
import { setGoogleMapError } from '@fiji/common/src/features/common/commonSlice';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { STATUS_COLORS, STATUS_PRIORITY } from '@fiji/common/src/constants';

export type LocationType = { lat: number | null; lng: number | null };

export function getIconPath(icon: any): string {
    const iconString = ReactDOMServer.renderToString(icon);
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(iconString, 'image/svg+xml');
    const iconPath = svgDoc.querySelector('path')?.getAttribute('d') as string;
    return iconPath;
}
// Get status counts from device list for InfoWindow display
type StatusCount = { status: string; count: number; color: string };

// Get icon component for each status
const getStatusIcon = (status: string, color: string): JSX.Element => {
    const iconStyle = { color, fontSize: '14px' };
    if (status === 'ALARM') return <NotificationsActive sx={iconStyle} />;
    if (status === 'WARNING') return <Warning sx={iconStyle} />;
    if (status === 'OFFLINE') return <CloudOffFilled sx={iconStyle} />;
    if (status === 'SNOOZED') return <Snooze sx={iconStyle} />;
    if (status === 'QUARANTINED') return <Block sx={iconStyle} />;
    if (status === 'INFORMATIONAL') return <Info sx={iconStyle} />;
    if (status === 'UPCOMING') return <Schedule sx={iconStyle} />;
    return <CheckCircle sx={iconStyle} />;
};

const getStatusCounts = (groupByStatus: Record<string, number>): StatusCount[] => {
    if (!groupByStatus || Object.keys(groupByStatus).length === 0) {
        return [];
    }

    // Sort by priority (higher priority first)
    return Object.entries(groupByStatus)
        .filter(([, count]) => count > 0)
        .map(([status, count]) => {
            const normalizedStatus = status.toUpperCase().replace(/\s+/g, '_');
            return {
                status: normalizedStatus,
                count,
                color: (STATUS_COLORS as any)[normalizedStatus] || '#007BC3',
            };
        })
        .sort((a, b) => STATUS_PRIORITY.indexOf(b.status) - STATUS_PRIORITY.indexOf(a.status));
};

// Get status counts from device's count object for single device InfoWindow display
const getDeviceCountStatus = (device: any): StatusCount[] => {
    if (!device?.count) {
        return [];
    }

    const countMapping: Record<string, string> = {
        alarmCount: 'ALARM',
        warningCount: 'WARNING',
        offlineCount: 'OFFLINE',
        snoozeCount: 'SNOOZED',
        informationalCount: 'INFORMATIONAL',
        upcomingCount: 'UPCOMING',
    };

    return Object.entries(countMapping)
        .filter(([key]) => device.count[key] > 0)
        .map(([key, status]) => ({
            status,
            count: device.count[key],
            color: (STATUS_COLORS as any)[status] || '#007BC3',
        }))
        .sort((a, b) => STATUS_PRIORITY.indexOf(b.status) - STATUS_PRIORITY.indexOf(a.status));
};

// Get marker color based on device status or device group statuses
const getMarkerColor = (deviceList: any[], defaultColor: string): string => {
    if (!deviceList || deviceList.length === 0) {
        return defaultColor;
    }

    // For a single device, return its status color
    if (deviceList.length === 1) {
        const device = deviceList[0];
        const status = device?.deviceStatus?.toUpperCase().replace(/\s+/g, '_');
        return (STATUS_COLORS as any)[status] || defaultColor;
    }

    // For multiple devices, find the most important status
    let highestPriority = -1;
    let selectedColor = defaultColor;

    deviceList.forEach((device: any) => {
        const status = device?.deviceStatus?.toUpperCase().replace(/\s+/g, '_');
        const priority = STATUS_PRIORITY.indexOf(status);

        if (priority > highestPriority) {
            highestPriority = priority;
            selectedColor = (STATUS_COLORS as any)[status] || defaultColor;
        }
    });
    return selectedColor;
};

export const fetchLocation = (callback?: any): Promise<{ lat: number; lng: number }> =>
    new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            if (callback) {
                callback('Geolocation is not supported by this browser.');
            }
            reject(new Error('Geolocation is not supported by this browser.'));
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position: GeolocationPosition) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                if (callback) {
                    callback(undefined);
                }
                resolve({ lat, lng });
            },
            (error: GeolocationPositionError) => {
                if (callback) {
                    callback(error?.message);
                }
                reject(new Error(error.message));
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    });

export const getMarkerUrl = (icon: any, themeColor: any): string =>
    `data:image/svg+xml,${encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="45" height="66" viewBox="0 0 45 63" fill="none"><path d="M45 22.4C45 9.92 35.0357 0 22.5 0C9.96428 0 0 9.92 0 22.4C0 39.04 22.5 64 22.5 64C22.5 64 45 39.04 45 22.4Z" fill="${themeColor}"/>${
            icon ?? ''
        }<g style="transform: translate(5px, 59px);"><ellipse cx="16.5" cy="4" rx="16.5" ry="4" fill="url(#paint0_radial_852_3674)"/><defs><radialGradient id="paint0_radial_852_3674" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(16.5 4) rotate(90) scale(4 16.5)"><stop stop-color="#424E54" stop-opacity="0.5"/><stop offset="1" stop-color="#424E54" stop-opacity="0"/></radialGradient></defs></g></svg>`
    )}`;

const getMarkerIcon = (themeColor: string, markerSelector: boolean, countKey?: string, obj?: any): string => {
    if (markerSelector) {
        return getMarkerUrl('<circle r="9" cx="23" cy="24" fill="#fff"/>', themeColor);
    }

    if (obj?.[countKey ?? 'count'] === 1) {
        return getMarkerUrl(
            `<g style="transform: translate(12px, 12px);"><path d="${getIconPath(
                <CustomIcon family="material-ui" iconName="CorporateFare" />
            )}" fill="#fff" /></g>`,
            themeColor
        );
    }

    return getMarkerUrl(undefined, themeColor);
};

type MapProps = {
    widgetData: any;
    clustering: boolean;
    minHeight?: string;
    mode?: string;
    onIdle?: any;
    countKey?: string;
    widgetId?: string;
    showInfoWindow: boolean;
    markerSelector?: boolean;
    isFullscreen?: boolean;
    showPinLabel?: boolean;
    customFullView?: boolean;
    center?: any;
    disabled?: boolean;
    isViewLocation?: boolean;
    isViewLayer?: boolean;
    isLoading?: boolean;
    isViewFullScreen?: boolean;
    onLocationFetch?: (lat: number, lng: number) => void;
};

const MapComponent = ({
    widgetData,
    clustering,
    minHeight,
    isLoading,
    onIdle,
    countKey = 'count',
    widgetId,
    // showInfoWindow = true,
    markerSelector = false,
    isFullscreen,
    showPinLabel,
    customFullView,
    center: centerData,
    mode,
    disabled,
    isViewLocation = true,
    isViewLayer = true,
    isViewFullScreen = true,
    onLocationFetch,
}: MapProps): JSX.Element => {
    const isMount = useIsMount();
    const navigate = useNavigate();
    const navigationBack = useNavigationBack();
    const handleNavigateBack = (): void => {
        navigationBack();
    };

    const theme: any = useTheme();
    const containerStyle = {
        ...(minHeight && { minHeight: minHeight }),
        width: '100%',
        height: mode?.includes('pinned') ? '425px' : '100%',
    };

    const MAX_ZOOM = 22;
    const MAX_DISPLAY_ZOOM = 22;
    const MAX_VIRTUAL_ZOOM = 22;
    const MIN_VIRTUAL_ZOOM = 1;

    const mapRef = useRef<any>(null);
    const [virtualZoom, setVirtualZoom] = React.useState(1);
    const virtualZoomRef = useRef(virtualZoom);
    const [isMapUnDirty, setIsMapUnDirty] = React.useState(false);

    const [center, setCenter] = useState<any>({ lat: 24.879, lng: -6.834 });

    const displayZoom = Math.min(virtualZoom, MAX_DISPLAY_ZOOM);
    virtualZoomRef.current = virtualZoom;

    const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));
    const [isFullScreen, setIsFullScreen] = React.useState(Boolean(isFullscreen));
    const [map, setMap] = React.useState<any>(null);
    const [selectedMarker, setSelectedMarker] = React.useState<any>();
    const [showInfoWindowMap, setShowInfoWindow] = useState(false);

    // Cache previous widget data to prevent markers from disappearing during loading
    const cachedWidgetDataRef = useRef<any>(null);
    if (widgetData?.secondary && widgetData.secondary.length > 0) {
        cachedWidgetDataRef.current = widgetData;
    }
    const displayWidgetData = isLoading && cachedWidgetDataRef.current ? cachedWidgetDataRef.current : widgetData;

    // Debounce timer ref to prevent duplicate API calls on zoom/pan
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Track last API call params to avoid duplicate calls with same bounds
    const lastBoundsRef = useRef<string | null>(null);

    // Track if a request is currently in-flight
    const isRequestInFlightRef = useRef<boolean>(false);

    // Track previous precision to determine if data should be appended
    const lastPrecisionRef = useRef<number | null>(null);

    // Reset widget-scoped refs when widgetId changes to prevent reusing data from a previous widget
    React.useEffect(() => {
        lastPrecisionRef.current = null;
        lastBoundsRef.current = null;
        cachedWidgetDataRef.current = null;
    }, [widgetId]);

    const googleMapLoaded = useTypedSelector((state) => state.common.googleMapLoaded);
    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const dispatch = useDispatch();

    const mapErrorHandler = (err: any): void => {
        dispatch(setGoogleMapError(err));
    };

    React?.useEffect(() => {
        if (centerData?.lat && centerData?.lng) {
            setCenter(centerData);
            setVirtualZoom(16);
            // Pan the map instance to the new center
            if (map) {
                map.panTo(centerData);
                map.setZoom(16);
            }
        }
    }, [centerData, map]);

    React.useEffect(() => {
        const handleEsc = (): void => {
            if (!document.fullscreenElement) {
                setIsFullScreen(false);
            }
        };
        document.body.addEventListener('fullscreenchange', handleEsc);

        return () => {
            document.body.removeEventListener('fullscreenchange', handleEsc);
        };
    }, []);

    const toggleFullScreen = async (): Promise<void> => {
        if (customFullView) {
            if (!isFullScreen) {
                navigate(widgetData?.route?.url || `/${currentRealmName}/map/${widgetData?.id}`, {
                    state: {
                        widgetName: widgetData?.widgetName,
                    },
                });
            } else {
                handleNavigateBack();
            }
        } else if (!isFullScreen) {
            await map?.getDiv().requestFullscreen();
            setIsFullScreen(true);
        } else if (document.fullscreenElement) {
            await document.exitFullscreen();
            setIsFullScreen(false);
        }
    };
    const onLoad = React.useCallback((property: any) => {
        setMap(property);
        mapRef.current = property;
    }, []);

    const onUnmount = React.useCallback(() => {
        mapRef.current = null;
    }, []);
    const bounds = new window.google.maps.LatLngBounds();

    // Debounced onIdle handler to prevent duplicate API calls
    const debouncedOnIdle = useCallback(() => {
        // // Skip if request is already in-flight
        // if (isRequestInFlightRef.current || isLoading) {
        //     return;
        // }

        // if (debounceTimerRef.current) {
        //     clearTimeout(debounceTimerRef.current);
        // }
        debounceTimerRef.current = setTimeout(() => {
            // // Double-check in-flight status after debounce
            // if (isRequestInFlightRef.current || isLoading) {
            //     return;
            // }

            if (!isMount && onIdle && map) {
                const currentPrecision = Math.round(Math.min(virtualZoomRef.current, MAX_VIRTUAL_ZOOM));
                const shouldAppendData =
                    lastPrecisionRef.current !== null && lastPrecisionRef.current === currentPrecision;

                const boundsData = {
                    widgetId: widgetId,
                    bounds: {
                        topLeft: {
                            lat: map?.getBounds()?.getNorthEast()?.lat(),
                            lng: map?.getBounds()?.getSouthWest()?.lng(),
                        },
                        bottomRight: {
                            lat: map?.getBounds()?.getSouthWest()?.lat(),
                            lng: map?.getBounds()?.getNorthEast()?.lng(),
                        },
                        precision: currentPrecision,
                    },
                    appendData: shouldAppendData,
                };

                // Create a key to compare with last call
                const boundsKey = JSON.stringify(boundsData);

                // Skip if same bounds as last call
                if (lastBoundsRef.current === boundsKey) {
                    return;
                }

                lastBoundsRef.current = boundsKey;
                lastPrecisionRef.current = currentPrecision;
                isRequestInFlightRef.current = true;
                onIdle(boundsData);
            }
        }, 500);
    }, [isMount, onIdle, widgetId, map, isLoading]);

    // Reset in-flight flag when loading completes
    React.useEffect(() => {
        if (!isLoading) {
            isRequestInFlightRef.current = false;
        }
    }, [isLoading]);

    // Cleanup debounce timer on unmount
    React.useEffect(
        () => () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        },
        []
    );

    React.useEffect(() => {
        if (!isMapUnDirty && widgetData?.secondary && widgetData?.secondary?.length === 0) {
            setVirtualZoom(3);
        }
    }, [widgetData]);

    // Keep the map in sync with displayZoom
    React.useEffect(() => {
        const mapInstance = mapRef.current;
        if (!mapInstance) return;
        const currentZoom = mapInstance.getZoom();
        if (currentZoom !== displayZoom) {
            mapInstance.setZoom(displayZoom);
        }
    }, [displayZoom]);

    // Handle zoom changed from map (scroll, pinch, fitBounds, etc.)
    const handleZoomChanged = useCallback(() => {
        const mapInstance = mapRef.current;
        if (!mapInstance) return;
        const currentZoom = mapInstance.getZoom();
        if (currentZoom === undefined) return;

        const next = Math.round(clamp(currentZoom, MIN_VIRTUAL_ZOOM, MAX_VIRTUAL_ZOOM));
        virtualZoomRef.current = next; // Update ref immediately so debouncedOnIdle reads the correct precision
        setVirtualZoom((prev) => (prev === next ? prev : next));
    }, []);

    // Intercept wheel to continue increasing virtual zoom beyond 22
    const handleWheel = useCallback(
        (e: React.WheelEvent) => {
            if (displayZoom < MAX_ZOOM) return;

            const wheelDeltaToZoomStep = (deltaY: number): number =>
                // Return +1 for zoom in, -1 for zoom out
                deltaY < 0 ? 1 : deltaY > 0 ? -1 : 0;
            const step = wheelDeltaToZoomStep(e.deltaY);
            if (step === 0) return;

            e.nativeEvent.stopPropagation();
            e.preventDefault();

            setVirtualZoom((prev) => {
                const next = clamp(prev + step, MIN_VIRTUAL_ZOOM, MAX_VIRTUAL_ZOOM);
                if (displayZoom > MAX_ZOOM && step > 0) {
                    return next;
                }
                return next;
            });
        },
        [displayZoom]
    );

    const zoomIn = useCallback(() => {
        setVirtualZoom((prev) => clamp(prev + 1, MIN_VIRTUAL_ZOOM, MAX_VIRTUAL_ZOOM));
    }, []);

    const zoomOut = useCallback(() => {
        setVirtualZoom((prev) => clamp(prev - 1, MIN_VIRTUAL_ZOOM, MAX_VIRTUAL_ZOOM));
    }, []);

    const handleMouseOver =
        (obj: any): (() => void) =>
        () => {
            setShowInfoWindow(true);
            setSelectedMarker(obj);
        };

    const handleMouseOut = (): void => {
        setShowInfoWindow(false);
        setSelectedMarker(undefined);
    };

    return googleMapLoaded && center?.lat ? (
        <div style={{ width: '100%', height: '100%', position: 'relative' }} onWheel={handleWheel}>
            <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={displayZoom}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    disableDefaultUI: true,
                    styles: mapStyles,
                    maxZoom: MAX_DISPLAY_ZOOM,
                    gestureHandling: 'greedy',
                }}
                onZoomChanged={handleZoomChanged}
                onBoundsChanged={(): void => setSelectedMarker(undefined)}
                onIdle={debouncedOnIdle}
            >
                {/* Always render markers using cached data during loading to prevent blinking */}
                <MarkerClusterer
                    averageCenter
                    gridSize={100}
                    styles={[
                        {
                            url: getMarkerIcon(theme?.palette?.primary?.main, markerSelector, countKey),
                            className: 'custom-cluser-marker-icon',
                            height: 48,
                            width: 48,
                            textColor: '#fff',
                            textSize: 20,
                        },
                    ]}
                >
                    {(clusterer): JSX.Element => (
                        <div>
                            {displayWidgetData?.secondary?.map((obj: any, index: number): JSX.Element => {
                                if (
                                    !isMapUnDirty &&
                                    obj.deviceCount === 1 &&
                                    displayWidgetData?.secondary?.length === 1
                                ) {
                                    setIsMapUnDirty(true);
                                    setCenter({ lat: obj.coordinates?.lat, lng: obj.coordinates?.lng });
                                    setVirtualZoom(17);
                                } else {
                                    bounds.extend({
                                        lat: parseFloat(obj.deviceList?.[0]?.location?.lat),
                                        lng: parseFloat(obj.deviceList?.[0]?.location?.lon),
                                    });
                                    bounds.extend({
                                        lat:
                                            parseFloat(obj.coordinates?.lat) +
                                            (parseFloat(obj.coordinates?.lat) -
                                                parseFloat(obj.deviceList?.[0]?.location?.lat)),
                                        lng:
                                            parseFloat(obj.coordinates?.lng) +
                                            (parseFloat(obj.coordinates?.lng) -
                                                parseFloat(obj.deviceList?.[0]?.location?.lon)),
                                    });
                                    if (displayWidgetData?.secondary?.length - 1 === index && !isMapUnDirty) {
                                        setIsMapUnDirty(true);
                                        if (
                                            Object.values(JSON.parse(JSON.stringify(bounds)))?.every(
                                                (item: any) => item !== null
                                            )
                                        ) {
                                            map.fitBounds(bounds);
                                        }
                                        setVirtualZoom(map?.getZoom());
                                    }
                                }
                                // Get the marker color based on device status
                                const markerColor = getMarkerColor(obj.deviceList || [], theme?.palette?.primary?.main);
                                const renderMarkerLabel = (): any => ({
                                    text:
                                        obj?.[countKey] > 1
                                            ? obj?.[countKey].toString()
                                            : showPinLabel
                                            ? obj.name
                                            : ' ',
                                    className: obj?.[countKey] > 1 ? '' : 'marker-label-with-avatar',
                                    color: obj?.[countKey] > 1 ? '#fff' : markerColor,
                                    ...(obj?.[countKey] === 1 && {
                                        fontSize: '14px',
                                        fontWeight: '600',
                                    }),
                                });
                                return (
                                    obj.coordinates?.lat && (
                                        <Marker
                                            key={`lat-${obj.coordinates?.lat}-lng-${obj.coordinates?.lng}`}
                                            position={obj.coordinates}
                                            onMouseOver={handleMouseOver(obj)}
                                            onMouseOut={handleMouseOut}
                                            icon={{
                                                scaledSize: new google.maps.Size(
                                                    obj?.[countKey] ? 48 : 32,
                                                    obj?.[countKey] ? 48 : 32
                                                ),
                                                url: getMarkerIcon(markerColor, markerSelector, countKey, obj),
                                                origin: new google.maps.Point(0, 0),
                                                anchor: new google.maps.Point(25, 50),
                                            }}
                                            animation={null}
                                            {...(clustering && { clusterer })}
                                            {...((obj?.[countKey] || obj.name) && {
                                                label: renderMarkerLabel(),
                                            })}
                                            clickable={!disabled}
                                            onClick={() => {
                                                if (obj?.[countKey] === 1) {
                                                    navigate(`/${currentRealmName}/device/${obj['id']}`);
                                                } else {
                                                    if (widgetId) {
                                                        navigate(`/${currentRealmName}/multiDevices/${widgetId}`, {
                                                            state: {
                                                                deviceIds: obj?.deviceList?.map(
                                                                    (device: any) => device?.id
                                                                ),
                                                                tileKey: obj?.tileKey,
                                                            },
                                                        });
                                                    } else {
                                                        navigate(`/${currentRealmName}/multiDevices`, {
                                                            state: {
                                                                deviceIds: obj?.deviceList?.map(
                                                                    (device: any) => device?.id
                                                                ),
                                                                tileKey: obj?.tileKey,
                                                            },
                                                        });
                                                    }
                                                }
                                                if (selectedMarker && showInfoWindowMap) {
                                                    setSelectedMarker(undefined);
                                                } else if (showInfoWindowMap) {
                                                    setSelectedMarker(obj);
                                                }
                                            }}
                                        >
                                            {selectedMarker &&
                                                showInfoWindowMap &&
                                                selectedMarker?.deviceCount === obj[countKey] &&
                                                selectedMarker?.name === obj?.name && (
                                                    <InfoWindow
                                                        position={obj.coordinates}
                                                        options={{
                                                            pixelOffset: {
                                                                height: 0,
                                                                width: 0,
                                                                equals: () => true,
                                                            },
                                                            minWidth: 100,
                                                            headerDisabled: true,
                                                        }}
                                                        onCloseClick={(): void => setSelectedMarker(undefined)}
                                                    >
                                                        <Stack sx={{ p: 1.5 }}>
                                                            <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
                                                                {obj[countKey] > 1
                                                                    ? `${obj[countKey]} Devices`
                                                                    : obj.name}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>
                                                                {obj[countKey] === 1 &&
                                                                obj?.deviceList?.[0]?.groupPath?.[1] !== undefined
                                                                    ? `${obj?.deviceList?.[0]?.groupPath?.[1]} > ${obj?.deviceList?.[0]?.groupPath?.[0]}`
                                                                    : obj?.deviceList?.[0]?.groupPath?.[0]}
                                                            </Typography>
                                                            {obj.deviceList && obj.deviceList.length > 0 && (
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1.5}
                                                                    sx={{ mt: 1 }}
                                                                    flexWrap="wrap"
                                                                >
                                                                    {(obj.deviceList.length === 1
                                                                        ? getDeviceCountStatus(obj.deviceList[0])
                                                                        : getStatusCounts(obj.groupByStatus)
                                                                    ).map((statusItem) => (
                                                                        <Stack
                                                                            key={statusItem.status}
                                                                            direction="row"
                                                                            alignItems="center"
                                                                            spacing={0.5}
                                                                        >
                                                                            {getStatusIcon(
                                                                                statusItem.status,
                                                                                statusItem.color
                                                                            )}
                                                                            <Typography
                                                                                sx={{
                                                                                    color: statusItem.color,
                                                                                    fontSize: '14px',
                                                                                    fontWeight: 600,
                                                                                }}
                                                                            >
                                                                                {statusItem.count}
                                                                            </Typography>
                                                                        </Stack>
                                                                    ))}
                                                                </Stack>
                                                            )}
                                                        </Stack>
                                                    </InfoWindow>
                                                )}
                                        </Marker>
                                    )
                                );
                            })}
                        </div>
                    )}
                </MarkerClusterer>

                <Stack direction={'row'} spacing={1} sx={{ position: 'absolute', right: '20px', bottom: '20px' }}>
                    {isViewLocation && (
                        <Box className="border-radius-4 bg-white box-shadow padding-6">
                            <IconButton
                                aria-label="delete"
                                disabled={disabled}
                                onClick={(): void => {
                                    void fetchLocation(mapErrorHandler).then(({ lat, lng }: any) => {
                                        setCenter({ lat: parseFloat(lat) ?? 0, lng: parseFloat(lng) ?? 0 });
                                        setVirtualZoom(16);
                                        onLocationFetch?.(lat, lng);
                                    });
                                }}
                            >
                                <MyLocation className="black-500" />
                            </IconButton>
                        </Box>
                    )}
                    {isViewLayer && (
                        <Box className={`border-radius-4 bg-white box-shadow padding-6 ${disabled ? '' : 'layers'}`}>
                            <Stack
                                direction={'row'}
                                sx={{ position: 'absolute', right: '132px', bottom: '46px' }}
                                className="default-satellite padding-bottom-10 bg-transparent"
                            >
                                <Stack direction={'row'} className="bg-transparent">
                                    <IconButton onClick={(): void => map?.setMapTypeId('roadmap')}>
                                        <Stack className="default" />
                                    </IconButton>
                                    <IconButton onClick={(): void => map?.setMapTypeId('hybrid')}>
                                        <Stack className="satellite" />
                                    </IconButton>
                                </Stack>
                            </Stack>
                            <IconButton aria-label="delete" disabled={disabled}>
                                <Layers className="black-500" />
                            </IconButton>
                        </Box>
                    )}
                    {isViewFullScreen && (
                        <Box className="border-radius-4 bg-white box-shadow padding-6">
                            <IconButton aria-label="delete" onClick={toggleFullScreen} disabled={disabled}>
                                {isFullScreen ? (
                                    <FullscreenExit className="black-500" />
                                ) : (
                                    <Fullscreen className="black-500" />
                                )}
                            </IconButton>
                        </Box>
                    )}
                    <Stack direction={'row'} className="border-radius-4 bg-white box-shadow">
                        <Box className="padding-6">
                            <IconButton
                                aria-label="delete"
                                onClick={zoomOut}
                                disabled={virtualZoom === MIN_VIRTUAL_ZOOM}
                            >
                                <ZoomOut className="black-500" />
                            </IconButton>
                        </Box>
                        <Box className="border-left-1 padding-6">
                            <IconButton
                                aria-label="delete"
                                onClick={zoomIn}
                                disabled={virtualZoom === MAX_VIRTUAL_ZOOM}
                            >
                                <ZoomIn className="black-500" />
                            </IconButton>
                        </Box>
                    </Stack>
                </Stack>
            </GoogleMap>
        </div>
    ) : (
        <Loader />
    );
};

const Component = (props: MapProps): JSX.Element => {
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const viewMapDetailsSystem = hasPermission('map-details_view-system-widgets');
    const viewMapDetailsUser = hasPermission('map-details_view-user-widgets');
    const viewMapDetailsSystemExpired = hasPermission('map-details_view-system-widgets_hasExpired');
    const viewMapDetailsUserExpired = hasPermission('map-details_view-user-widgets_hasExpired');
    return (
        <Card sx={{ height: '100%', ...(props?.mode?.includes('pinned') && { boxShadow: 'none' }) }}>
            {!props?.mode?.includes('pinned') && <WidgetHeader mode={props?.mode} widgetData={props?.widgetData} />}
            {props?.mode !== 'edit' && <Divider />}
            <CardContent
                className="padding-0"
                sx={{
                    height: `calc(100% - ${
                        props?.mode === 'edit' || props?.widgetData?.viewAllButton ? '97px' : '49px'
                    })`,
                    position: 'relative',
                }}
            >
                <MapComponent
                    {...props}
                    showPinLabel={props?.widgetData?.showPinLabels}
                    customFullView
                    disabled={!props?.mode?.includes?.('view') || (!viewMapDetailsSystem && !viewMapDetailsUser)}
                />
            </CardContent>
            {!props?.mode?.includes('pinned') &&
                (viewMapDetailsSystem ||
                    viewMapDetailsUser ||
                    viewMapDetailsUserExpired ||
                    viewMapDetailsSystemExpired) && (
                    <WidgetFooter
                        mode={props?.mode}
                        widgetData={props?.widgetData}
                        ids={[]}
                        disabled={!viewMapDetailsSystem || !viewMapDetailsUser}
                        actionBtnName={<CustomTransComponent translationKey={'MAP:VIEW_FULL_MAP'} />}
                    />
                )}
        </Card>
    );
};
export default React.memo(Component);
export { MapComponent };
